import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { SiteSettingsProvider } from "./contexts/SiteSettingsContext";
import ScrollToTopButton from "./components/base/ScrollToTopButton";
import ScrollToTop from "./components/base/ScrollToTop";
import { PageErrorBoundary } from "./components/base/ErrorBoundary";
import { initRealtimeManager, reconnectRealtime } from "./lib/realtimeManager";
import { usePageTracking } from "./hooks/usePageTracking";

const AuthProvider = lazy(() =>
  import("./contexts/AuthContext").then((module) => ({ default: module.AuthProvider }))
);

// مكوّن داخلي لتتبع الصفحات (يحتاج BrowserRouter)
function PageTracker() {
  usePageTracking();
  return null;
}

// مكوّن يعيد الاتصال عند عودة الإنترنت
function RealtimeReconnect() {
  useEffect(() => {
    // Effects run after the first paint, keeping the realtime websocket off the
    // critical path for the header and hero.
    initRealtimeManager();
    const handleOnline = () => {
      console.log("[Realtime] Network restored — reconnecting...");
      reconnectRealtime();
    };
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, []);
  return null;
}

// Authentication is a CMS concern. Public visitors should not download the
// auth implementation or query the complete admin_users table during startup.
function RouteProviders({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const content = <SiteSettingsProvider>{children}</SiteSettingsProvider>;

  if (!pathname.startsWith("/admin")) return content;
  return (
    <Suspense fallback={<div className="min-h-screen bg-white" />}>
      <AuthProvider>{content}</AuthProvider>
    </Suspense>
  );
}

function App() {
  return (
    <PageErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter basename={__BASE_PATH__}>
          <RouteProviders>
            <ScrollToTop />
            <PageTracker />
            <RealtimeReconnect />
            <AppRoutes />
            <ScrollToTopButton />
          </RouteProviders>
        </BrowserRouter>
      </I18nextProvider>
    </PageErrorBoundary>
  );
}

export default App;
